import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import { BUTTON_CONST } from '@/src/constants/buttons';
import dayjs from 'dayjs';
import {
  PostRecyclingCouponRequestBody,
  PutRecyclingCouponRequestBody,
} from '@/src/apis/rest/recyclingCoupon.dtos';
import { useController, useForm } from 'react-hook-form';
import { Form, FormField } from '@/src/components/ui/form';
import FormContent from '@/src/components/molecule/FormContent';
import Input from '@/src/components/atom/Input';
import { DateRange } from 'react-day-picker';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import RadioGroup from '@/src/components/atom/RadioGroup';
import ImageUpload from '@/src/components/atom/ImageUpload';
import { Media } from '@/src/models/MeidaDto';
import IconButton from '@/src/components/atom/Button/IconButton';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { useMutation } from '@tanstack/react-query';
import { postRecyclingCouponAPI } from '@/src/apis/rest/recyclingCoupon';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ImageUploadInfo from '@/components/molecule/ImageUploadInfo';
import { useRef } from 'react';
import { APIError } from '@/src/models/common/apiError';
import { OPTIONS_CONST } from '@/src/constants/options';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { usePrompt } from '@/src/hooks/usePrompt';

interface RecyclingCouponCreateModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOk: (id: number) => void;
}

const formSchema = () => {
  return z.object({
    sponsorName: z.string().min(1),
    onAt: z.string().min(1),
    offAt: z.string().min(1),
    couponMediaId: z.number(),
    isExposed: z.boolean(),
    exposeService: z.string(),
  });
};

const DEFAULT_VALUE = {
  sponsorName: '',
  exposeService: 'APP' as const,
  isExposed: true,
  postedAt: {
    from: undefined,
    to: undefined,
  },
};

export default function RecyclingCouponCreateModal({
  open: openProps,
  onOpenChange: onOpenChangeProps,
  onOk,
}: RecyclingCouponCreateModalProps) {
  // util
  const {
    Dialog: BlockAlertDialog,
    isOpen: isBlockAlertOpen,
    openDialog: openBlockAlertDialog,
    closeDialog: closeBlockAlertDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const {
    Dialog: DuplicateErrorDialog,
    isOpen: isDuplicateErrorOpen,
    openDialog: openDuplicateErrorDialog,
    closeDialog: closeDuplicateErrorDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // form
  const form = useForm<
    PutRecyclingCouponRequestBody & {
      postedAt: DateRange;
      couponMedia: Media;
    }
  >({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUE,
    reValidateMode: 'onSubmit',
  });
  const { field: couponMediaField } = useController({
    name: 'couponMedia',
    control: form.control,
  });
  const { field: onAtField } = useController({
    name: 'onAt',
    control: form.control,
  });
  const { field: offAtField } = useController({
    name: 'offAt',
    control: form.control,
  });

  // mutation
  const putRecyclingCouponMutation = useMutation({
    mutationFn: postRecyclingCouponAPI,
  });

  // event
  const handleCloseModal = () => {
    if (form.formState.isDirty) {
      openBlockAlertDialog();
    } else {
      onOpenChangeProps(false);
    }
  };

  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  const handleSubmit = (values: PostRecyclingCouponRequestBody) => {
    putRecyclingCouponMutation.mutate(values, {
      onSuccess: response => {
        toast({
          variant: 'success',
          title: TOAST_CONST.saveSuccess.title,
          description: TOAST_CONST.saveSuccess.description,
        });

        onOpenChangeProps(false);
        onOk(response.id);
      },
      onError: e => {
        const { error } = e as unknown as APIError;

        if (error === 'CANNOT_CREATE_RECYCLING_COUPON_IN_DUPLICATE_PERIOD') {
          openDuplicateErrorDialog();
        } else {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        }
      },
    });
  };
  const handleBlockModalOk = () => {
    onOpenChangeProps(false);
  };

  // render
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs().isAfter(date, 'D')) return true;

    return false;
  };
  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.cancel}
        </Button>
        <Button
          type="button"
          className="flex-1"
          disabled={!form.formState.isValid}
          onClick={openSaveDialog}
          isLoading={putRecyclingCouponMutation.isPending}
        >
          {BUTTON_CONST.save}
        </Button>
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          style: {
            width: 'calc(100vw - 24px)',
          },
          className: 'max-w-[697px]',
        }}
        title="배송비 쿠폰 등록"
        footer={renderFooter()}
      >
        <Form {...form}>
          <form
            ref={formRef}
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="sponsorName"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="후원사 명" isRequired error={error}>
                    <Input
                      containerClassName="flex-1"
                      onClear={() => field.onChange('')}
                      placeholder="후원사 명을 입력해주세요"
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="postedAt"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="게시 기간" isRequired error={error}>
                    <DateRangePicker
                      {...field}
                      onChange={e => {
                        field.onChange(e);

                        onAtField.onChange(
                          e?.from ? dayjs(e?.from).format('YYYY-MM-DD') : '',
                        );
                        offAtField.onChange(
                          e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                        );
                      }}
                      dateDisabledMatcher={getDateDisabledMatcher}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="isExposed"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="노출 여부" error={error}>
                    <RadioGroup
                      optionList={[
                        { label: OPTIONS_CONST.exposed, value: 'true' },
                        { label: OPTIONS_CONST.notExposed, value: 'false' },
                      ]}
                      rootProps={{
                        value: field.value ? 'true' : 'false',
                        onValueChange: (e: string) =>
                          field.onChange(e === 'true'),
                      }}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="exposeService"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="노출 범위" error={error}>
                    <RadioGroup
                      optionList={[
                        { label: OPTIONS_CONST.onlyApp, value: 'APP' },
                        { label: OPTIONS_CONST.onlyWeb, value: 'WEB' },
                      ]}
                      rootProps={{
                        value: field.value,
                        onValueChange: field.onChange,
                      }}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="couponMediaId"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent
                    label="쿠폰 이미지"
                    isRequired
                    error={error}
                    defaultMessage={
                      <ImageUploadInfo widthSize={80} heightSize={80} />
                    }
                  >
                    <ImageUpload
                      width={128}
                      height={128}
                      widthText={80}
                      heightText={80}
                      maxImageSizeMb={5}
                      imageClassName="object-cover"
                      value={couponMediaField.value?.bufferFullUrl}
                      onChange={e => {
                        field.onChange(Number(e.id));
                        couponMediaField.onChange(e);
                      }}
                      topAccessary={({ handleEdit, handleRemove }) => {
                        return (
                          <>
                            <div className="flex gap-x-1">
                              <IconButton
                                buttonSize="xSmall"
                                buttonColor="outline"
                                lucideIconProps={{ name: 'Pencil' }}
                                onClick={() => {
                                  handleEdit();
                                }}
                              />
                              <IconButton
                                buttonSize="xSmall"
                                buttonColor="outline"
                                lucideIconProps={{ name: 'X' }}
                                onClick={() => {
                                  handleRemove();
                                  field.onChange(undefined);
                                }}
                              />
                            </div>
                          </>
                        );
                      }}
                    />
                  </FormContent>
                );
              }}
            />
          </form>
        </Form>
        <BlockAlertDialog
          open={isBlockAlertOpen}
          onOpenChange={closeBlockAlertDialog}
          onCancel={() => {
            closeBlockAlertDialog();
          }}
          onOk={handleBlockModalOk}
          title="취소"
        >
          변경사항은 저장되지 않습니다. 취소하시겠습니까?
        </BlockAlertDialog>
        <DuplicateErrorDialog
          open={isDuplicateErrorOpen}
          onOpenChange={closeDuplicateErrorDialog}
          onCancel={() => {
            closeDuplicateErrorDialog();
          }}
          onOk={closeDuplicateErrorDialog}
          title="배송비 쿠폰 중복 불가"
          cancelButtonProps={{
            className: 'hidden',
          }}
        >
          <p className="whitespace-pre-wrap">
            선택한 기간동안 앱에 노출 중인 배송비 쿠폰이 있습니다.
            <br />
            노출 중인 배송비 쿠폰을 삭제하거나 기간을 변경하여 다시
            등록해주세요.
          </p>
        </DuplicateErrorDialog>
      </Dialog>

      {/* 모달 */}
      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.save}
          isLoading={putRecyclingCouponMutation.isPending}
        />
      )}
    </>
  );
}
